import { watch, type App } from 'vue'
import PrimeVue, { type PrimeVueLocaleOptions } from 'primevue/config'
import Aura from '@primevue/themes/aura'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import { i18n } from '@/plugins/i18n'
import { primeVueLocaleFor } from './locales'
import { useBusinessLocaleStore } from '@/modules/settings/store/businessLocale'

const usePrimeVue = (app: App): void => {
  app.use(PrimeVue, {
    theme: {
      preset: Aura,
      options: {
        darkModeSelector: '.app-dark',
      },
    },
  })
  app.use(ConfirmationService)
  app.use(ToastService)
  // Registered globally so `v-tooltip` works anywhere. Without this the
  // directive is silently inert: Vue drops an unknown directive rather than
  // failing, so icon-only buttons stay unexplained and nothing warns.
  app.directive('tooltip', Tooltip)
  followAppLanguage(app)
}

/**
 * Keeps PrimeVue's own strings — month names, paginator controls, password
 * strength, empty lists — in the language the rest of the console is in.
 * They come from the library, not from our message catalogue, so nothing in
 * the i18n files can reach them and they stay English until told otherwise.
 */
function followAppLanguage(app: App): void {
  const config = app.config.globalProperties.$primevue.config
  // Captured BEFORE anything overrides it: these are PrimeVue's shipped
  // English strings, and they are the only way back when the user switches
  // the console to English.
  const english = { ...config.locale } as PrimeVueLocaleOptions
  // Pinia is installed before this plugin (main.ts), so the store resolves.
  const businessLocale = useBusinessLocaleStore(app.config.globalProperties.$pinia)

  watch(
    [i18n.global.locale, () => businessLocale.dateFormat],
    ([locale, dateFormat]) => {
      // The strings follow the UI language; the DATE SHAPE follows the
      // business's country, like money does. Every DatePicker without an
      // explicit `dateFormat` reads it from here.
      config.locale = { ...primeVueLocaleFor(locale, english), dateFormat }
    },
    // Applied at once rather than on the next change: the login screen shows
    // a password field before anyone touches the language switch, and its
    // prompt is the first text a new user reads.
    { immediate: true },
  )
}

export default usePrimeVue
